import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Input, Label } from '@/ui/primitives';
import { ArrowRight, Crosshair, Search } from 'lucide-react';
import { formatCep, isValidCep, normalizeCep } from '@/domain/cep';
import type { Dictionary } from '@/i18n';

type CepFormProps = {
  dict: Dictionary;
  onSubmit: (cep: string) => void;
  onUseLocation: () => void;
  isBusy: boolean;
  /* `compact` is the embed: one row rather than a stack, because the panel it sits in has
     to hold the answer underneath it inside the height of one section. It asks for the
     same thing and validates it the same way — only the chrome is smaller. */
  variant?: 'page' | 'compact';
};

export const CepForm = ({
  dict,
  onSubmit,
  onUseLocation,
  isBusy,
  variant = 'page',
}: CepFormProps) => {
  const [value, setValue] = useState('');
  const [showError, setShowError] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidCep(value)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    onSubmit(normalizeCep(value));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(formatCep(event.target.value));
    setShowError(false);
  };

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {/* The label is the placeholder here, so the field still has to name itself
                to a screen reader. Same string either way. */}
            <Input
              id="cep"
              name="cep"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              enterKeyHint="search"
              aria-label={dict.form.cepLabel}
              placeholder={dict.form.cepPlaceholder}
              value={value}
              hasError={showError}
              errorMessage={showError ? dict.form.invalidCep : false}
              onChange={handleChange}
              /* Square, like the button beside it — the two read as one control, and a
                 square button against a rounded field is worse than leaving both rounded.
                 The `!` is not decoration: `rounded-none` and the primitive's `rounded-lg`
                 are both radius utilities, so which one wins is decided by the order
                 Tailwind emits them in, not by the order they appear in `class`. Today
                 `rounded-none` lands later and wins; a minor upgrade could reorder that
                 and round the corners back with nothing in the diff to blame. */
              className="h-11 rounded-none! text-base"
            />
          </div>
          {/* Square, and cyan rather than neon yellow. Inside the vammo.com home this
              button sits among the site's own controls, which are square and use the Vammo
              cyan; the neon yellow is the landing page's accent and reads as a foreign
              element there. The standalone page keeps both. Mauricio's call. */}
          <Button
            type="submit"
            disabled={isBusy}
            aria-label={dict.form.submit}
            className="h-11 w-11 shrink-0 rounded-none! p-0"
          >
            <span className="flex items-center text-neon-blue">
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </span>
          </Button>
        </div>

        <Button
          type="button"
          variant="ghost"
          disabled={isBusy}
          onClick={onUseLocation}
          className="h-8 self-start px-2 text-[13px] font-semibold"
        >
          <Crosshair className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {isBusy ? dict.form.busy : dict.form.useLocation}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cep" className="text-sm">
          {dict.form.cepLabel}
        </Label>
        <Input
          id="cep"
          name="cep"
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          enterKeyHint="search"
          placeholder={dict.form.cepPlaceholder}
          value={value}
          hasError={showError}
          /* vammo-ui renders this as a role="alert" paragraph and flips aria-invalid,
             so the error is announced without a second element to wire up. */
          errorMessage={showError ? dict.form.invalidCep : false}
          onChange={handleChange}
          className="h-12 text-lg"
        />
      </div>

      {/* Neon yellow is the Vammo signature accent and the brand doc says to spend
          it sparingly, so it appears once: on the one action this page exists for.
          The colour sits on a span, not on the Button: vammo-ui ships the Tailwind v3
          preflight unlayered, and its `button { color: inherit }` outranks any
          `@layer utilities` text colour. See AGENTS.md. */}
      <Button type="submit" disabled={isBusy} className="h-12 w-full text-base font-bold">
        <span className="flex items-center text-neon-yellow">
          <Search className="mr-2 h-4 w-4" aria-hidden="true" />
          {isBusy ? dict.form.busy : dict.form.submit}
        </span>
      </Button>

      <Button
        type="button"
        variant="ghost"
        disabled={isBusy}
        onClick={onUseLocation}
        className="h-11 w-full text-sm"
      >
        <Crosshair className="mr-2 h-4 w-4" aria-hidden="true" />
        {dict.form.useLocation}
      </Button>
    </form>
  );
};
